<template>
  <div data-rmpdev-selector="update-user-form">
    <p v-if="questionsToComplete.length > 0">
      To register your interest in this job, we need to complete your profile.
      It will only take 17 seconds! (We timed it.) Just enter your information in the fields below.
    </p>
    <div
      v-if="questionsToComplete.indexOf('fullname') > -1"
      class=""
    >
      <fullname-incremental-field
        ref="ryi_fullname"
        :validate-all="shouldValidate"
      />
    </div>
    <div
      v-if="questionsToComplete.indexOf('uk_based') > -1"
      class=""
    >
      <location-incremental-field
        ref="ryi_uk_based"
        :validate-all="shouldValidate"
      />
    </div>
    <div
      v-if="questionsToComplete.indexOf('gender') > -1"
      class=""
    >
      <gender-incremental-field
        ref="ryi_gender"
        :validate-all="shouldValidate"
      />
    </div>
    <div>
      <div
        v-if="questionsToComplete.indexOf('institution_start_year') > -1"
        class=""
      >
        <div>
          <institution-start-year-incremental-field
            ref="ryi_institution_start_year"
            :validate-all="shouldValidate"
          />
        </div>
      </div>
    </div>
    <div
      v-if="questionsToComplete.indexOf('job_types') > -1"
      class="Form-field"
    >
      <job-types-incremental-field
        ref="ryi_job_types"
        :validate-all="shouldValidate"
        :user="user"
      />
    </div>
    <div v-if="questionsToComplete.indexOf('job_roles') > -1">
      <job-roles-incremental-field
        ref="ryi_job_roles"
        :validate-all="shouldValidate"
      />
    </div>
    <div
      v-if="questionsToComplete.indexOf('subscriptions') > -1"
      class="Form-field"
    >
      <subscriptions-incremental-field
        ref="ryi_subscriptions"
        :validate-all="shouldValidate"
      />
    </div>
    <button
      v-if="!isItemShortlisted"
      :disabled="loading"
      :class="{ 'spinner': loading }"
      class="Button Button--primary"
      @click.prevent="handleSubmit"
    >
      Register your interest
    </button>
    <div v-else>
      <div
        class="Notice Notice--success"
      >
        <span>You’ve successfully registered your interest in this job. Good life decision. We’ll send word as soon as applications are open.</span>
      </div>
      <br>
      <button
        :disabled="loading"
        :class="{ 'spinner': loading }"
        class="Button Button--grey"
        @click.prevent="unregisterInterest"
      >
        I'm no longer interested
      </button>
    </div>
  </div>
</template>

<script>
import GenderIncrementalField from '@/components/incremental-data/fields/gender-incremental-field.vue';
import FullnameIncrementalField from '@/components/incremental-data/fields/fullname-incremental-field';
import LocationIncrementalField from '@/components/incremental-data/fields/location-incremental-field';
import SubscriptionsIncrementalField from '@/components/incremental-data/fields/subscriptions-incremental-field';
import InstitutionStartYearIncrementalField from '@/components/incremental-data/fields/institution-start-year-incremental-field';
import JobTypesIncrementalField from '@/components/incremental-data/fields/job-types-incremental-field.vue';
import JobRolesIncrementalField from '@/components/incremental-data/fields/job-roles-incremental-field.vue';

export default {
  components: {
    GenderIncrementalField,
    FullnameIncrementalField,
    LocationIncrementalField,
    SubscriptionsIncrementalField,
    InstitutionStartYearIncrementalField,
    JobTypesIncrementalField,
    JobRolesIncrementalField,
  },

  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
    loading: {
      type: Boolean,
      default: false,
    },
    validateAll: {
      type: Boolean,
      default: false,
    },
    serverValidationErrors: {
      type: Object,
      default: () => ({}),
    },
    isItemShortlisted: {
      type: Boolean,
      default: false,
    },
    entities: {
      type: Object,
      default: () => ({}),
    },
    questionsToComplete: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      shouldValidate: false,
      inputs: {
        fullname: null,
        password: null,
        gender: null,
        uk_based: null,
        postcode: null,
        subscriptions: null,
        institution_start_year: null,
        jobs_subscription: false,
        competitions_subscription: false,
        courses_subscription: false,
        job_types: [],
        job_roles: [],
      },
    };
  },

  watch: {
    entities: {
      handler() {
        if (!this.entities.jobRoles || window.registrationTags === undefined) return;

        const values = Object.values(this.entities.jobRoles);
        const jobRolesArray = values.reduce((acc, item) => {
          return acc.concat(item);
        }, []);

        const registrationTags = window.registrationTags.map((windowTag) => {
          return jobRolesArray.find((jobRole) => jobRole.code === windowTag.tag_value);
        });

        this.inputs.jobRoles = registrationTags;
      },
      deep: true,
    },
  },

  methods: {
    handleSubmit() {
      this.populateModelFromInputFields();

      const isFormInvalid = this.validateInputs();

      if (isFormInvalid) {
        Object.keys(this.$refs).forEach((ref) => {
          if (this.$refs[ref].v$) this.$refs[ref].v$.$validate();
        });

        this.shouldValidate = true;
        return false;
      }

      const userPayload = this.questionsToComplete.reduce((payload, question) => {
        switch (question) {
          case 'subscriptions':
            payload.subscription_decision = 1;
            Object.keys(this.inputs[question]).forEach((key) => {
              payload[key] = this.inputs[question][key] ? 1 : 0;
            });
            break;
          case 'job_roles':
            payload[question] = [...this.inputs[question]];
            break;
          case 'job_types':
            payload[question] = this.inputs[question].map((item) => item.id);
            break;
          case 'postcode':
            payload[question] = this.inputs[question] ?? null;
            break;
          default:
            payload[question] = this.inputs[question].value || this.inputs[question];
        }

        return payload;
      }, {});

      this.$emit('register-interest-form:submit', { userPayload });
      return true;
    },

    populateModelFromInputFields() {
      const nestedValueFields = ['fullname', 'gender', 'uk_based'];

      this.questionsToComplete.forEach((question) => {
        let field = null;
        if (question === 'postcode') {
          field = this.$refs.ryi_uk_based.fields;
        } else {
          field = this.$refs[`ryi_${question}`].fields;
        }

        if (question === 'postcode') {
          this.inputs[question] = field.postcode;
        } else if (question === 'institution_start_year') {
          this.inputs[question] = field[question] ? field[question].value : null;
        } else if (question === 'job_types') {
          const label = 'jobTypes';
          this.inputs[question] = field[label];
        } else if (question === 'job_roles') {
          // A computed property is returned from the job roles field therefore we need to access the value property
          const { jobRoles } = field.jobRoles.value;
          this.inputs[question] = jobRoles;
        } else if (nestedValueFields.includes(question)) {
          this.inputs[question] = field[question];
        } else {
          this.inputs[question] = field;
        }
      });
    },

    validateInputs() {
      const fieldsToValidate = this.questionsToComplete
        .map((field) => {
          // postcode should not be validatated if user is not uk_based
          if (field === 'postcode' && this.inputs.uk_based === 0) {
            return -1;
          }
          return this.inputs[field];
        })
        .filter((field) => field !== -1);

      const hasEmptyValue = (el) => {
        if (Array.isArray(el)) {
          return el.length === 0;
        }
        return el === null || el === undefined || el === '';
      };
      return fieldsToValidate.some(hasEmptyValue);
    },

    unregisterInterest() {
      if (this.loading) return;

      this.$emit('register-interest-form:unregister-interest');
    },
  },
};
</script>
<style lang="scss" scoped>
  :deep(.IncrementalData-supportingText) {
    display: none;
  }
</style>
